import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mic,
  Square,
  Sparkles,
  Play,
  Pause,
  RotateCcw,
  Home,
  Volume2,
} from "lucide-react";

import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import { api, backendUrl } from "../api";

// Max recording length (matches user requirement).
const MAX_RECORD_SEC = 120;

/* -----------------------------------------------------------------------
 * Shared AudioPlayer — mirrors the one in LanguageResult so both screens
 * look/feel identical. Duplicated here (rather than exporting from the
 * other file) to keep this new feature self-contained and avoid touching
 * the existing Summary flow.
 * ---------------------------------------------------------------------*/
function MentorAudioPlayer({ src, autoPlay = false, testid = "mentor-audio-player" }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrent(a.currentTime);
    const onMeta = () => {
      const d = isFinite(a.duration) ? a.duration : 0;
      setDuration(d);
      setReady(true);
      if (autoPlay) {
        // Try to autoplay. Some browsers block until user interaction; we
        // catch and stay silent — user can tap play.
        a.play().then(() => setPlaying(true)).catch(() => { /* ignore */ });
      }
    };
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, [src, autoPlay]);

  const play = async () => {
    try { await audioRef.current?.play(); setPlaying(true); }
    catch (_e) { /* ignore */ }
  };
  const pause = () => { audioRef.current?.pause(); setPlaying(false); };
  const seek = (e) => {
    const a = audioRef.current; if (!a) return;
    const v = Number(e.target.value); a.currentTime = v; setCurrent(v);
  };
  const fmt = (s) => {
    if (!isFinite(s) || s < 0) s = 0;
    const m = Math.floor(s / 60); const r = Math.floor(s % 60);
    return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  };

  return (
    <div data-testid={testid} className="mt-4 bg-white rounded-3xl p-4 border-4 border-white shadow-[0_6px_0_rgba(0,0,0,0.08)]">
      <audio ref={audioRef} src={src} preload="metadata" />
      <div className="flex items-center gap-3">
        {playing ? (
          <button data-testid={`${testid}-pause`} onClick={pause}
            className="w-12 h-12 rounded-full bg-amber-400 text-white flex items-center justify-center shadow-[0_3px_0_rgba(0,0,0,0.15)] active:translate-y-0.5">
            <Pause size={22} strokeWidth={3} />
          </button>
        ) : (
          <button data-testid={`${testid}-play`} onClick={play} disabled={!ready}
            className="w-12 h-12 rounded-full bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-400 text-white flex items-center justify-center shadow-[0_3px_0_rgba(0,0,0,0.15)] active:translate-y-0.5">
            <Play size={22} strokeWidth={3} />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <input type="range" min={0} max={duration || 0} step={0.1} value={current} onChange={seek}
            disabled={!ready || !duration} className="w-full accent-sky-500" />
          <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 mt-1 tabular-nums">
            <span>{fmt(current)}</span>
            <span>{fmt(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Convert Gemini's HTML fragment (`<b>...</b>`) into safe React nodes.
 * We ONLY allow <b> — every other character is rendered as plain text.
 * ---------------------------------------------------------------------*/
function renderWithBold(text) {
  if (!text) return null;
  const parts = [];
  const rx = /<b>([\s\S]*?)<\/b>/gi;
  let last = 0;
  let m;
  while ((m = rx.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<b key={m.index} className="text-rose-600 underline decoration-dotted decoration-rose-400">{m[1]}</b>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/* -----------------------------------------------------------------------
 * Recording animation — pulsing ripples around a mic while active.
 * ---------------------------------------------------------------------*/
function RecordingRipples() {
  return (
    <div className="relative w-40 h-40 flex items-center justify-center">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="absolute inset-0 rounded-full bg-rose-400/25"
          animate={{ scale: [1, 1.9], opacity: [0.6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut", delay: i * 0.6 }}
        />
      ))}
      <div className="relative w-24 h-24 rounded-full bg-rose-500 flex items-center justify-center shadow-[0_10px_0_rgba(0,0,0,0.15)] border-4 border-white">
        <Mic size={44} strokeWidth={2.5} className="text-white" />
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Processing animation — mirrors the ProcessingScreen used by MathCapture
 * and LanguageCapture so the experience feels consistent.
 * ---------------------------------------------------------------------*/
function AnalyzingScreen({ lang }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  return (
    <div className="page-shell flex flex-col items-center justify-center text-center" data-testid="mentor-analyzing">
      <motion.div
        animate={{ y: [0, -22, 0], rotate: [0, -10, 10, 0] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
        className="w-32 h-32 rounded-full bg-gradient-to-br from-sky-400 to-violet-400 flex items-center justify-center shadow-[0_10px_0_rgba(0,0,0,0.12)] border-4 border-white"
      >
        <Sparkles size={56} strokeWidth={2.5} className="text-white" />
      </motion.div>
      <h2 className="font-heading text-3xl font-bold mt-8 text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">
        {t(lang, "speech_mentor_analyzing")}
      </h2>
      <p className="text-base text-cyan-50/95 mt-2 font-semibold drop-shadow-[0_2px_8px_rgba(15,23,42,0.45)]">{t(lang, "almost_there")}</p>
      <div className="mt-8 bg-white rounded-3xl px-8 py-4 border-4 border-slate-900/10 shadow-[0_6px_0_rgba(0,0,0,0.08)]">
        <div className="font-heading text-5xl font-bold text-slate-800 tabular-nums">{mm}:{ss}</div>
        <p className="text-xs text-cyan-100/85 font-bold mt-1 drop-shadow-[0_2px_6px_rgba(15,23,42,0.4)]">usually ~80s</p>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Main page.  Phases: 'intro' → 'recording' → 'processing' → 'result'.
 * ---------------------------------------------------------------------*/
export default function SpeechMentor() {
  const navigate = useNavigate();
  const { lang, config, updateBalance } = useAuth();
  const [phase, setPhase] = useState("intro");
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  // 'checking' | 'ready' | 'unavailable' — we probe the welcome-audio URL with
  // a HEAD request before mounting <audio> so browsers that don't reliably
  // fire onError (iOS Safari, some Android WebViews) never show a broken
  // play button. Playing "unavailable" simply hides the audio element while
  // the welcome text alone stays on screen.
  const [welcomeAudioStatus, setWelcomeAudioStatus] = useState("checking");
  // The URL that will actually be attached to the <audio src=...>. We prefer
  // the static-file mirror (served in ~5 ms with zero API cost) and only fall
  // back to the on-demand generation endpoint if the static file is missing
  // (e.g. `doi.wav` was not shipped in the initial bundle).
  const [welcomeAudioSrc, setWelcomeAudioSrc] = useState(null);

  const welcomeAudioRef = useRef(null);
  const [welcomePaused, setWelcomePaused] = useState(true);

  const mediaStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);

  const staticWelcomeUrl = `${backendUrl}/static/welcome/${lang}.wav`;
  const fallbackWelcomeUrl = `${backendUrl}/api/lang-speech-mentor/welcome-audio/${lang}.wav`;
  const welcomeText = config?.speech_mentor_welcome?.[lang] || config?.speech_mentor_welcome?.en || "";
  const cost = Number(config?.speech_mentor_price ?? 3);

  const stopMediaTracks = () => {
    try { mediaStreamRef.current?.getTracks?.().forEach((t2) => t2.stop()); } catch (_e) { /* ignore */ }
    mediaStreamRef.current = null;
  };
  const clearTicker = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  useEffect(() => {
    // Cleanup on unmount.
    return () => {
      clearTicker();
      stopMediaTracks();
      try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); } catch (_e) { /* ignore */ }
    };
  }, []);

  // Resolve which welcome-audio URL to use for the current language.
  //  1. Try the static-file mirror first (`/static/welcome/{lang}.wav`).
  //     HEAD probe accepts the response if it looks like real audio. Content
  //     types vary by host: FastAPI serves `audio/wav`, nginx often serves
  //     `.wav` as `application/octet-stream`, and CRA's SPA fallback serves
  //     the tiny (~5 KB) `index.html` as `text/html`. We accept the first
  //     two and reject the third via a size heuristic.
  //  2. If that fails, HEAD-probe the generation endpoint as an on-demand
  //     fallback. This covers languages we haven't shipped a static file
  //     for yet (e.g. `doi.wav`) — the endpoint synthesizes + caches it.
  //  3. If both fail, hide the player entirely; the welcome text alone
  //     keeps the child oriented.
  useEffect(() => {
    let cancelled = false;
    setWelcomeAudioStatus("checking");
    setWelcomeAudioSrc(null);

    const probe = async (url) => {
      try {
        const r = await fetch(url, { method: "HEAD", cache: "no-store" });
        const contentType = (r.headers.get("content-type") || "").toLowerCase();
        const contentLength = Number(r.headers.get("content-length") || "0");
        // Accept anything that plausibly LOOKS like a real audio file:
        //   - explicit audio/* MIME, or
        //   - binary blob (application/octet-stream) served by many CDNs
        //     for .wav files, or
        //   - >20 KB payload (CRA SPA fallback is ~5 KB; a real welcome WAV
        //     is 400 KB+).
        const looksLikeAudio =
          contentType.includes("audio") ||
          contentType.includes("octet-stream") ||
          contentLength > 20000;
        // Rejection: HTML fallback for missing files (CRA/nginx SPA route).
        const isHtmlFallback = contentType.includes("html");
        console.info("[speech-mentor] audio probe", {
          url,
          status: r.status,
          ok: r.ok,
          contentType,
          contentLength,
          looksLikeAudio,
          isHtmlFallback,
        });
        return r.ok && looksLikeAudio && !isHtmlFallback;
      } catch (e) {
        console.warn("[speech-mentor] audio probe failed", url, e?.message);
        return false;
      }
    };

    (async () => {
      console.info("[speech-mentor] resolving welcome audio", { lang, staticWelcomeUrl, fallbackWelcomeUrl });
      if (await probe(staticWelcomeUrl)) {
        if (cancelled) return;
        setWelcomeAudioSrc(staticWelcomeUrl);
        setWelcomeAudioStatus("ready");
        return;
      }
      if (await probe(fallbackWelcomeUrl)) {
        if (cancelled) return;
        setWelcomeAudioSrc(fallbackWelcomeUrl);
        setWelcomeAudioStatus("ready");
        return;
      }
      if (!cancelled) {
        console.warn("[speech-mentor] no welcome audio source available for lang=", lang);
        setWelcomeAudioStatus("unavailable");
      }
    })();

    return () => { cancelled = true; };
  }, [lang, staticWelcomeUrl, fallbackWelcomeUrl]);

  const startRecording = async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error(t(lang, "speech_mentor_mic_unsupported"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      // Pick the most compatible mime type available on this device.
      let mime = "audio/webm;codecs=opus";
      if (typeof MediaRecorder !== "undefined") {
        const tryTypes = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
        for (const t2 of tryTypes) {
          if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t2)) { mime = t2; break; }
        }
      }
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        stopMediaTracks();
        await uploadRecording(blob, mime);
      };
      recorderRef.current = rec;
      rec.start(250);
      setElapsed(0);
      setPhase("recording");
      tickRef.current = setInterval(() => {
        setElapsed((e) => {
          const nxt = e + 1;
          if (nxt >= MAX_RECORD_SEC) {
            // Auto-stop when reaching the max.
            stopRecording();
          }
          return nxt;
        });
      }, 1000);
    } catch (e) {
      console.error("[speech-mentor] getUserMedia failed", e);
      toast.error(t(lang, "speech_mentor_mic_denied"));
    }
  };

  const stopRecording = () => {
    clearTicker();
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch (_e) { /* ignore */ }
    setPhase("processing");
  };

  const uploadRecording = async (blob, mime) => {
    try {
      const ext = mime.startsWith("audio/mp4") ? "m4a"
                : mime.startsWith("audio/ogg") ? "ogg"
                : "webm";
      const filename = `recording-${Date.now()}.${ext}`;
      const form = new FormData();
      form.append("file", blob, filename);
      form.append("language", lang || "en");
      form.append("age_group", "10-14 years");
      const r = await api.post("/generate/lang-speech-mentor", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      const data = r.data;
      if (typeof data.wallet_balance === "number") {
        try { updateBalance(data.wallet_balance); } catch (_e) { /* ignore */ }
      }
      setResult(data);
      setPhase("result");
    } catch (e) {
      const detail = e.response?.data?.detail || "Speech mentor failed";
      toast.error(detail);
      setPhase("intro");
    }
  };

  /* --------------------------- render ---------------------------------*/
  if (phase === "processing") return <AnalyzingScreen lang={lang} />;

  if (phase === "recording") {
    const remaining = Math.max(0, MAX_RECORD_SEC - elapsed);
    const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
    const ss = String(remaining % 60).padStart(2, "0");
    return (
      <div className="page-shell flex flex-col items-center text-center" data-testid="speech-mentor-recording">
        <div className="w-full flex items-center justify-between mb-4">
          <div className="w-12" />
          <h2 className="font-heading text-2xl font-bold text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">
            {t(lang, "lang_speech_mentor")}
          </h2>
          <div className="w-12" />
        </div>
        <div className="mt-6"><RecordingRipples /></div>
        <p className="mt-6 font-heading text-lg font-bold text-white drop-shadow-[0_2px_8px_rgba(15,23,42,0.5)]">
          {t(lang, "speech_mentor_recording")}
        </p>
        <div className="mt-4 bg-white rounded-3xl px-8 py-3 border-4 border-rose-200 shadow-[0_6px_0_rgba(0,0,0,0.08)]" data-testid="mentor-timer">
          <div className="font-heading text-4xl font-bold text-rose-600 tabular-nums">{mm}:{ss}</div>
        </div>
        <button
          data-testid="speech-mentor-stop-btn"
          onClick={stopRecording}
          className="mt-8 w-full bg-slate-800 hover:bg-slate-900 text-white font-heading text-xl font-bold uppercase tracking-wide py-4 rounded-full btn-squish flex items-center justify-center gap-2"
        >
          <Square size={22} strokeWidth={3} /> {t(lang, "speech_mentor_tap_to_stop")}
        </button>
      </div>
    );
  }

  if (phase === "result" && result) {
    const audioSrc = result.audio_b64 ? `data:${result.audio_mime || "audio/wav"};base64,${result.audio_b64}` : null;
    const lines = (result.actual_article || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    return (
      <div className="page-shell" data-testid="speech-mentor-result">
        <div className="flex items-center justify-between mb-4">
          <button data-testid="back-btn" onClick={() => navigate("/language")}
            className="p-3 rounded-full bg-white border-2 border-slate-900/10 btn-squish">
            <ArrowLeft size={22} strokeWidth={2.5} className="text-slate-700" />
          </button>
          <h2 className="font-heading text-2xl font-bold text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">{t(lang, "lang_speech_mentor")}</h2>
          <div className="w-12" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-5 border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.08)]"
        >
          <h3 className="font-heading text-lg font-bold text-slate-700 mb-3 flex items-center gap-2">
            <Volume2 size={18} strokeWidth={2.5} className="text-sky-500" />
            {t(lang, "speech_mentor_your_speech")}
          </h3>
          {lines.length ? (
            <ul data-testid="mentor-actual-article" className="space-y-2.5">
              {lines.map((line, i) => (
                <li key={i} className="flex items-start gap-3 text-base text-slate-800 font-medium leading-relaxed">
                  <span aria-hidden className="mt-2 w-2.5 h-2.5 shrink-0 rounded-full bg-gradient-to-br from-sky-400 to-violet-500 ring-2 ring-sky-200 shadow-[0_2px_0_rgba(0,0,0,0.1)]" />
                  <span className="min-w-0">{renderWithBold(line)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500 font-semibold">—</p>
          )}
        </motion.div>

        {audioSrc ? (
          <MentorAudioPlayer src={audioSrc} autoPlay testid="mentor-improved-audio" />
        ) : (
          <div className="mt-4 text-center text-xs text-slate-500 font-semibold" data-testid="mentor-audio-unavailable">
            {result.audio_error ? `Audio: ${result.audio_error}` : t(lang, "audio_unavailable")}
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 gap-3">
          <button
            data-testid="speech-mentor-another-btn"
            onClick={() => { setResult(null); setPhase("intro"); }}
            className="bg-white border-2 border-slate-900/10 text-slate-700 font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
          >
            <RotateCcw size={16} strokeWidth={2.75} className="shrink-0" />
            <span>{t(lang, "practice_again")}</span>
          </button>
          <button
            data-testid="speech-mentor-home-btn"
            onClick={() => navigate("/")}
            className="bg-purple-400 hover:bg-purple-500 text-white font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
          >
            <Home size={16} strokeWidth={2.75} className="shrink-0" />
            <span>{t(lang, "home")}</span>
          </button>
        </div>
      </div>
    );
  }

  // phase === "intro"
  return (
    <div className="page-shell" data-testid="speech-mentor-intro">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="back-btn" onClick={() => navigate("/language")}
          className="p-3 rounded-full bg-white border-2 border-slate-900/10 btn-squish">
          <ArrowLeft size={22} strokeWidth={2.5} className="text-slate-700" />
        </button>
        <h2 className="font-heading text-2xl font-bold text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">{t(lang, "lang_speech_mentor")}</h2>
        <div className="w-12" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-sky-100 via-white to-violet-100 rounded-3xl p-5 border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.08)]"
      >
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 shrink-0 rounded-2xl bg-sky-400 flex items-center justify-center shadow-[0_3px_0_rgba(0,0,0,0.12)]">
            <Sparkles size={22} strokeWidth={2.75} className="text-white" />
          </div>
          <p data-testid="speech-mentor-welcome-text"
             className="text-base text-slate-800 font-semibold leading-relaxed whitespace-pre-wrap">
            {welcomeText || "…"}
          </p>
        </div>
        {/* Autoplay welcome audio only if the server confirmed (via HEAD)
            that a real audio/* response is available. Everything else keeps
            the <audio> tag out of the DOM so no broken play button ever
            appears — the on-screen welcome text is enough to guide the child. */}
        {welcomeAudioStatus === "ready" && welcomeAudioSrc && (
          <>
            {/* Native controls so the child can always tap play manually — the
                autoplay call below may be blocked by iOS Safari / any browser
                without prior user interaction. We log the play() promise so
                the flow is fully debuggable in production. */}
            <audio
              ref={welcomeAudioRef}
              key={welcomeAudioSrc}
              data-testid="speech-mentor-welcome-audio"
              src={welcomeAudioSrc}
              autoPlay
              controls
              preload="auto"
              onCanPlay={() => {
                const a = welcomeAudioRef.current;
                console.info("[speech-mentor] audio canplay", {
                  lang, src: welcomeAudioSrc,
                  readyState: a?.readyState, networkState: a?.networkState,
                  duration: a?.duration,
                });
              }}
              onPlay={() => setWelcomePaused(false)}
              onPause={() => setWelcomePaused(true)}
              onEnded={() => setWelcomePaused(true)}
              onError={() => {
                const a = welcomeAudioRef.current;
                console.error("[speech-mentor] audio error", {
                  lang, src: welcomeAudioSrc,
                  readyState: a?.readyState, networkState: a?.networkState,
                  error: a?.error && { code: a.error.code, message: a.error.message },
                });
                setWelcomeAudioStatus("unavailable");
              }}
              onLoadedMetadata={() => {
                const a = welcomeAudioRef.current;
                if (!a) return;
                // Attempt to autoplay ONLY after metadata is loaded so we know
                // the resource is real. Log the promise result so we can see
                // exactly why iOS blocks it (NotAllowedError vs NotSupportedError).
                const p = a.play();
                if (p && typeof p.then === "function") {
                  p.then(() => {
                    console.info("[speech-mentor] audio.play() resolved", { lang });
                  }).catch((err) => {
                    console.warn("[speech-mentor] audio.play() rejected — will require tap", {
                      lang, name: err?.name, message: err?.message,
                    });
                    setWelcomePaused(true);
                  });
                }
              }}
              className="w-full mt-4 accent-sky-500"
            />
            {/* Big, obvious tap-to-play button that overrides browser autoplay
                blocks. Only shown when audio is not currently playing so the
                child gets one clear affordance even on iOS. */}
            {welcomePaused && (
              <button
                type="button"
                data-testid="speech-mentor-welcome-play-btn"
                onClick={async () => {
                  const a = welcomeAudioRef.current;
                  if (!a) return;
                  try {
                    a.currentTime = 0;
                    await a.play();
                  } catch (err) {
                    console.error("[speech-mentor] manual play failed", {
                      lang, name: err?.name, message: err?.message,
                    });
                  }
                }}
                className="mt-3 w-full bg-sky-500 hover:bg-sky-600 text-white font-heading font-bold uppercase tracking-tight text-sm py-3 rounded-full btn-squish inline-flex items-center justify-center gap-2"
              >
                <Volume2 size={18} strokeWidth={2.75} />
                <span>{t(lang, "speech_mentor_play_welcome")}</span>
              </button>
            )}
          </>
        )}
      </motion.div>

      <div className="mt-8 flex flex-col items-center text-center">
        <p className="text-sm text-cyan-50/95 font-semibold mb-4 drop-shadow-[0_2px_8px_rgba(15,23,42,0.45)]">
          {t(lang, "speech_mentor_tap_to_record")}
        </p>
        <button
          data-testid="speech-mentor-mic-btn"
          onClick={startRecording}
          className="w-28 h-28 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow-[0_10px_0_rgba(0,0,0,0.15)] border-4 border-white btn-squish"
        >
          <Mic size={54} strokeWidth={2.5} />
        </button>
        <p className="mt-3 text-xs text-cyan-100/85 font-semibold drop-shadow-[0_2px_6px_rgba(15,23,42,0.4)]">
          ₹{cost} · max 2 min
        </p>
      </div>
    </div>
  );
}
